import { useEffect, useLayoutEffect, useRef, useState } from "react";
import AboutPage from "./about/AboutPage";
import HelloPage from "./hello/HelloPage";
import SiteNav from "./nav/SiteNav";
import ConnectPage from "./connect/ConnectPage";
import PortfolioPage from "./portfolio/PortfolioPage";
import ResumePage from "./resume/ResumePage";
import "./tokens.css";
import "./App.css";
import "./site.css";

const SECTIONS = ["hello", "about", "resume", "portfolio", "connect"] as const;

if ("scrollRestoration" in history) {
  history.scrollRestoration = "manual";
}
type SectionId = (typeof SECTIONS)[number];

function isSection(id: string): id is SectionId {
  return (SECTIONS as readonly string[]).includes(id);
}

export default function App() {
  const barRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState<SectionId>(() => {
    const id = window.location.hash.replace("#", "");
    return isSection(id) ? id : "hello";
  });

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const apply = () => {
      document.documentElement.style.setProperty(
        "--site-nav",
        `${Math.ceil(bar.getBoundingClientRect().height)}px`,
      );
    };
    const observer = new ResizeObserver(apply);
    observer.observe(bar);
    apply();
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!isSection(id) || id === "hello") return;
    const node = document.getElementById(id);
    node?.scrollIntoView({ behavior: "auto" });
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const id = window.location.hash.replace("#", "");
      if (id === "hello" || !isSection(id)) {
        window.scrollTo(0, 0);
        return;
      }
      document.getElementById(id)?.scrollIntoView({ behavior: "auto" });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const restoreHelloHeader = () => {
      if (window.location.hash.replace("#", "") !== "hello") return;
      const hello = document.getElementById("hello");
      const name = hello?.querySelector(".name-lockup");
      const bar = document.querySelector(".site-bar");
      if (!hello || !name || !bar) return;
      const navBottom = bar.getBoundingClientRect().bottom;
      const nameTop = name.getBoundingClientRect().top;
      const helloBottom = hello.getBoundingClientRect().bottom;
      const stillInHello = helloBottom > navBottom;
      if (stillInHello && nameTop < navBottom - 4 && window.scrollY < window.innerHeight) {
        window.scrollTo(0, 0);
      }
    };
    const onResize = () => {
      requestAnimationFrame(() => requestAnimationFrame(restoreHelloHeader));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const nodes = SECTIONS.map((id) => document.getElementById(id)).filter(
      (node): node is HTMLElement => Boolean(node),
    );
    const activeRef = { id: active };
    let lastScroll = window.scrollY;

    const navBottom = () => barRef.current?.getBoundingClientRect().bottom ?? 0;

    const sectionAtNav = (): SectionId => {
      const scrollingDown = window.scrollY >= lastScroll - 1;
      lastScroll = window.scrollY;
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const fromEnd = Math.max(0, maxScroll - window.scrollY);
      // Near the end of the page the last sections cannot reach the nav,
      // so the activation line eases downward. Elsewhere it stays just
      // under the bar. The up/down offset avoids flicker at the boundary.
      const slack = Math.max(0, window.innerHeight * 0.55 - fromEnd);
      const line = navBottom() + (scrollingDown ? 12 : 64) + slack;
      let current: SectionId = "hello";
      for (const node of nodes) {
        if (node.getBoundingClientRect().top <= line && isSection(node.id)) {
          current = node.id;
        }
      }
      return current;
    };

    const publish = (id: SectionId) => {
      if (activeRef.id !== id) {
        activeRef.id = id;
        setActive(id);
      }
      const next = `#${id}`;
      if (window.location.hash !== next) {
        window.history.replaceState(null, "", next);
      }
    };

    const sync = () => {
      publish(sectionAtNav());
    };

    sync();
    const intersection = new IntersectionObserver(() => sync(), {
      root: null,
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    nodes.forEach((node) => intersection.observe(node));
    window.addEventListener("scroll", sync, { passive: true });
    document.addEventListener("scroll", sync, { passive: true, capture: true });
    return () => {
      intersection.disconnect();
      window.removeEventListener("scroll", sync);
      document.removeEventListener("scroll", sync, { capture: true });
    };
  }, []);

  return (
    <div className="site">
      <header className="site-bar" ref={barRef}>
        <SiteNav current={active} />
      </header>
      <HelloPage />
      <AboutPage />
      <ResumePage />
      <PortfolioPage />
      <ConnectPage />
      <footer className="site-footer">
        <div className="site-footer-inner">
          <p>© 2026 Arielle Schussler</p>
          <a href="#hello">Back to top</a>
        </div>
      </footer>
    </div>
  );
}
